import type { IsoDate, IsoMonth, Timestamp } from '@/lib/api/types';

// Date-only values ('YYYY-MM-DD') are calendar dates: formatted in UTC so they
// never shift a day. Timestamps are shown in the school's time zone. Month
// names come from the locale: ar-IQ → "تشرين الأول", ar-LY → "أكتوبر".

const formatters = new Map<string, Intl.DateTimeFormat>();

function dtf(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options);
    formatters.set(key, f);
  }
  return f;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Parses 'YYYY-MM-DD' (or the date part of a midnight-UTC timestamp) to a UTC Date. */
export function parseIsoDate(value: IsoDate | Timestamp): Date {
  const datePart = value.slice(0, 10);
  return new Date(`${datePart}T00:00:00.000Z`);
}

export type DateStyle = 'long' | 'medium' | 'short';

const DATE_STYLES: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  long: { day: 'numeric', month: 'long', year: 'numeric' },
  medium: { day: 'numeric', month: 'short', year: 'numeric' },
  short: { day: 'numeric', month: 'numeric', year: 'numeric' },
};

/**
 * A calendar date. Accepts 'YYYY-MM-DD' or a timestamp the API uses for a
 * DATE column ('2026-09-01T00:00:00.000Z'); both show the stored day.
 */
export function formatDate(value: IsoDate | Timestamp | null | undefined, locale: string, style: DateStyle = 'long'): string {
  if (!value) return '';
  const d = parseIsoDate(value);
  if (Number.isNaN(d.getTime())) return value;
  return dtf(locale, { ...DATE_STYLES[style], timeZone: 'UTC' }).format(d);
}

/** 'YYYY-MM' (or any date in it) → "تشرين الأول ٢٠٢٦" / "أكتوبر 2026" / "October 2026". */
export function formatMonth(value: IsoMonth | IsoDate | Timestamp | null | undefined, locale: string): string {
  if (!value) return '';
  const d = new Date(`${value.slice(0, 7)}-01T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return value;
  return dtf(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d);
}

/** A moment in time, in the school's zone. */
export function formatDateTime(value: Timestamp | null | undefined, locale: string, timeZone: string, style: DateStyle = 'medium'): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return dtf(locale, { ...DATE_STYLES[style], hour: 'numeric', minute: '2-digit', timeZone }).format(d);
}

export function formatTime(value: Timestamp | null | undefined, locale: string, timeZone: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return dtf(locale, { hour: 'numeric', minute: '2-digit', timeZone }).format(d);
}

/** Weekday + date, for page subtitles ("الخميس ٢٢ تشرين الأول"). */
export function formatWeekday(value: IsoDate, locale: string): string {
  return dtf(locale, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(parseIsoDate(value));
}

/** Today's date in a time zone as 'YYYY-MM-DD'. */
export function todayIn(timeZone: string, now: Date = new Date()): IsoDate {
  return dtf('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export function addMonths(month: IsoMonth, n: number): IsoMonth {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
}

export function isIsoDate(value: string): boolean {
  if (!DATE_ONLY.test(value)) return false;
  const d = parseIsoDate(value);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}
